import { Link } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { trackCtaClick } from "@/lib/marketing-events";

const PRODUCT_LINKS = [
  { hash: "customer-management", label: "Customer Management" },
  { hash: "revenue-management", label: "Revenue Management" },
  { hash: "task-management", label: "Task Management" },
];

/** Shared chrome for the public marketing pages (home and For Business). */
export function MarketingHeader({ signedIn }: { signedIn: boolean }) {
  return (
    <header className="border-b border-border/60">
      <div className="mx-auto grid max-w-5xl grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-4 sm:flex sm:justify-between sm:px-6">
        <Link to="/" className="min-w-0 truncate font-display text-sm font-bold">
          <span className="text-primary">◆</span> MyPriorities
        </Link>
        <nav className="flex shrink-0 items-center gap-3 whitespace-nowrap text-sm sm:gap-5">
          <div className="hidden sm:block">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground">
                Product <ChevronDown className="size-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {PRODUCT_LINKS.map((item) => (
                  <DropdownMenuItem key={item.hash} asChild>
                    <Link to="/" hash={item.hash}>
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <Link
            onClick={() => trackCtaClick("see_how_it_works", "header")}
            to="/how-it-works"
            className="hidden text-muted-foreground transition-colors hover:text-foreground sm:inline"
            activeProps={{ className: "text-foreground" }}
          >
            How it works
          </Link>

          <Link
            to="/business"
            hash="plans"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            Pricing
          </Link>

          <Link to="/auth" className="font-medium hover:underline">
            {signedIn ? "Open hub" : "Sign in"}
          </Link>

          <Button asChild size="sm" className="shrink-0">
            <Link to="/auth" onClick={() => trackCtaClick("start_for_free", "header")}>
              Start for free
            </Link>
          </Button>
        </nav>
      </div>
    </header>

  );
}

export function MarketingFooter() {
  return (
    <footer className="border-t border-border/60">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-6 py-8 text-sm text-muted-foreground">
        <p>
          <span className="text-primary">◆</span> MyPriorities — customers, revenue and work in one
          place.
        </p>

        <div className="flex gap-4">
          <Link to="/how-it-works" className="hover:text-foreground">
            How it works
          </Link>
          <Link to="/business" hash="plans" className="hover:text-foreground">
            Pricing
          </Link>
          <Link to="/auth" className="hover:text-foreground">
            Sign in
          </Link>
        </div>
      </div>
    </footer>
  );
}

export function Section({
  eyebrow,
  children,
  className = "",
}: {
  eyebrow?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`mx-auto max-w-5xl px-6 py-16 ${className}`}>
      {eyebrow ? <p className="label-caps">{eyebrow}</p> : null}
      {children}
    </section>
  );
}

export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-4xl">{children}</h2>
  );
}

export function Lede({ children }: { children: ReactNode }) {
  return <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{children}</p>;
}
